import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Users, Wallet, Snowflake } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { FiNav, FiSwedishLink } from "@/components/FiNav";
import { fiOpasFaq, fiOpasSections } from "@/data/fi-opas";

const TITLE = "Käytetyn asuntoauton ostajan opas – hinta ja tarkastus";
const DESC =
  "Ostajan opas suomeksi: mikä ikä ja pohjaratkaisu kannattaa, mitä omistaminen kustantaa, talvivarustelu ja tarkastuslista. Kalix, Ruotsi.";
const URL = "https://www.akhusbilfritid.se/fi/ostajan-opas";

const CARDS = [
  { icon: <Users size={18} />, t: "Käyttö ensin", d: "Yöt, henkilömäärä, vuodenaika ja matkatyyppi ratkaisevat mallin." },
  { icon: <Wallet size={18} />, t: "4–8 vuotta", d: "Taloudellisesti paras ostoikä — suurin arvonalenema takana." },
  { icon: <Snowflake size={18} />, t: "Talvivarustelu", d: "Eristetyt säiliöt ja kaksoislattia pohjoisen käyttöön." },
];

export const Route = createFileRoute("/fi/ostajan-opas")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(fiOpasFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:alt", content: "Käytetyn asuntoauton ostajan opas – AK Husbil & Fritid, Kalix" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }, { rel: "alternate", hrefLang: "fi-FI", href: URL }],
    scripts: [
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://www.akhusbilfritid.se/fi" },
            { "@type": "ListItem", position: 2, name: "Ostajan opas", item: URL },
          ],
        }),
      },
    ],
  }),
  component: FiOpasPage,
});

function FiOpasPage() {
  return (
    <>
      <PageHeader
        label="Ostajan opas · Palvelemme suomeksi"
        title={
          <>
            Käytetyn asuntoauton <span className="font-italic-display text-accent">ostajan opas</span>.
          </>
        }
        sub="Mikä ikä ja pohjaratkaisu kannattaa, mitä omistaminen todella kustantaa ja mitä katsomiskäynnillä tarkastetaan. Kirjoitettu sen perusteella, mitä suomalaiset asiakkaamme meiltä kysyvät."
      />

      <FiNav />

      <section className="container-edge py-10">
        <div className="grid gap-4 sm:grid-cols-3">
          {CARDS.map((c) => (
            <div key={c.t} className="rounded-3xl border border-border bg-card p-6">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-accent/15 text-accent">{c.icon}</span>
              <h2 className="mt-4 font-display text-xl">{c.t}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      <HubEditorial
        eyebrow="Ostajan opas"
        sections={fiOpasSections}
        faq={fiOpasFaq}
        faqTitle="Usein kysytyt kysymykset"
      />

      <section className="container-edge pb-14">
        <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
          <p className="eyebrow">Lue seuraavaksi</p>
          <h2 className="mt-3 font-display text-2xl">Tuonti, tarkastus ja varasto</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/fi/tuonti"
              className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Tuonti & autovero <ArrowRight size={14} />
            </Link>
            <Link
              to="/fi/kosteusmittaus"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
            >
              Kosteusmittaus <ArrowRight size={14} />
            </Link>
            <Link
              to="/fi/asuntoautot"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
            >
              Asuntoautot <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <FiSwedishLink to="/guider" label="Guider på svenska" />
    </>
  );
}
